import { FC } from "react";
import { gql } from "@apollo/client";
import Link from "next/link";
import { Button } from "@src/lib/ui/Button";
import Text from "@src/lib/ui/Text";
import UserBubbleCheckFillIcon from "@src/lib/icons/18px/user-bubble-check-fill";
import useActionPermissions from "@src/features/app/hooks/useActionPermissions";
import { useFeatureFlag } from "@src/features/app/hooks/useFeatureFlag";
import {
  HomeSection,
  HomeSectionCountBadge,
} from "@src/features/home/HomeSection";
import { Ff } from "@src/types.generated";
import {
  CheckInsToFacilitateCardQuery,
  useCheckInsToFacilitateCardQuery,
} from "./CheckInsToFacilitateCard.generated";

const MAX_NAMES = 3;

type PendingCheckIn =
  CheckInsToFacilitateCardQuery["MyPendingCheckinsToFacilitate"]["objects"][number];

// First names of the people in the pending list, each once, in the list's
// order (the server's: earliest due date first).
export const distinctFirstNames = (checkIns: PendingCheckIn[]): string[] => {
  const seen = new Set<number>();
  const names: string[] = [];
  for (const { user } of checkIns) {
    if (seen.has(user.id)) continue;
    seen.add(user.id);
    names.push(user.firstName);
  }
  return names;
};

// "Bernardo, Kelly, Sean, and 2 others are ready for Check-ins"
export const readyForCheckInsText = (names: string[]): string => {
  const shown = names.slice(0, MAX_NAMES);
  const others = names.length - shown.length;
  if (others > 0) {
    shown.push(`${others} ${others === 1 ? "other" : "others"}`);
  }
  const verb = shown.length === 1 ? "is" : "are";
  let list: string;
  if (shown.length <= 2) {
    list = shown.join(" and ");
  } else {
    list = `${shown.slice(0, -1).join(", ")}, and ${shown[shown.length - 1]}`;
  }
  return `${list} ${verb} ready for Check-ins`;
};

// The home page's Check-ins row: badged with the number of pending check-ins,
// naming who is waiting, with a link to the full list. Renders nothing when
// there are none, like mobile's home card.
const CheckInsToFacilitateCard: FC = () => {
  const enabled = useFeatureFlag(Ff.WebFormCompletion);
  const { userActionPermissions } = useActionPermissions();
  const canFacilitate = enabled && !!userActionPermissions?.verifySkills;
  const { data } = useCheckInsToFacilitateCardQuery({
    skip: !canFacilitate,
    fetchPolicy: "cache-and-network",
  });
  const pending = data?.MyPendingCheckinsToFacilitate;
  if (!canFacilitate || !pending || pending.totalCount === 0) return null;
  return (
    <HomeSection
      title="Check-ins"
      titleHref="/check-ins"
      titleMeta={<HomeSectionCountBadge count={pending.totalCount} />}
    >
      <div
        className="flex items-center gap-3 rounded-2xl border border-border-primary bg-bg-primary p-3"
        data-testid="check-ins-to-facilitate"
      >
        <div className="flex size-8.5 shrink-0 items-center justify-center rounded-full bg-status-warning-bg">
          <UserBubbleCheckFillIcon className="text-tag-yellow" />
        </div>
        <Text
          variant="fine-print-description"
          className="min-w-0 flex-1 truncate text-text-primary"
        >
          {readyForCheckInsText(distinctFirstNames(pending.objects))}
        </Text>
        <Button variant="outline" size="sm" asChild>
          <Link href="/check-ins">View</Link>
        </Button>
      </div>
    </HomeSection>
  );
};

// Unpaginated: the names need everyone in the list, and the badge counts
// check-ins (rows), not people.
gql`
  query CheckInsToFacilitateCard {
    MyPendingCheckinsToFacilitate {
      totalCount
      objects {
        id
        user {
          id
          firstName
        }
      }
    }
  }
`;

export default CheckInsToFacilitateCard;
